import { PlacesAddressType } from "@/types/google/placesApi";
import {
  AddressFields,
  AddressWithLocation,
  defaultAddress,
  GeoLocation,
} from "@/types/location";
import { logger } from "@sentry/nextjs";

export type AutocompletePrediction = {
  distance: number; //meters
  formattedAddress: string;
  placeId: string;
  fillPlace: () => Promise<AddressWithLocation>;
};

export async function autocompleteAddress(
  address: string,
  language?: string,
  type?: PlacesAddressType | PlacesAddressType[],
  countryCode?: string,
  distanceFrom?: GeoLocation,
): Promise<AutocompletePrediction[]> {
  if (address === "") {
    return [];
  }

  // Documentation for this: https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompleteService
  const placesAPI = (await google.maps.importLibrary(
    "places",
  )) as google.maps.PlacesLibrary;

  const sessionToken = new placesAPI.AutocompleteSessionToken();

  const { suggestions } =
    await placesAPI.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: address,
      sessionToken: sessionToken,
      language: language,
      locationBias: new google.maps.LatLngBounds(
        {
          lat: 35.46067,
          lng: -14.326172,
        },
        {
          lat: 71.910888,
          lng: 45.878906,
        },
      ),
      origin: new google.maps.LatLng(
        distanceFrom
          ? {
              lat: distanceFrom.latitude,
              lng: distanceFrom.longitude,
            }
          : {
              lat: 50.066827,
              lng: 10.084653,
            },
      ),
      includedPrimaryTypes:
        type !== undefined
          ? typeof type === "string"
            ? [type]
            : [...type]
          : undefined,
      region: countryCode,
    });

  const results = suggestions
    .filter((sugg, i) => {
      if (!sugg.placePrediction) {
        logger.warn(
          "suggestion " +
            i +
            " for input " +
            address +
            " was filtered because placePredicion was null",
        );
        console.warn("suggestion fiiltered because placePredicion was null");
      }
      return !!sugg.placePrediction;
    })
    .map((sugg) => {
      return {
        distance: sugg.placePrediction?.distanceMeters ?? -1,
        formattedAddress: sugg.placePrediction!.text.text,
        placeId: sugg.placePrediction!.placeId,
        fillPlace: async () => {
          const res = sugg.placePrediction!.toPlace();
          await res.fetchFields({
            fields: [
              "formattedAddress",
              "addressComponents",
              "location",
              "types",
            ],
          });

          return {
            address: res.formattedAddress!,
            address_fields: AddressComponentsToAddressWithLocation(
              res.addressComponents!,
            ),
            placeId: sugg.placePrediction!.placeId!,
            location: {
              latitude: res.location!.lat(),
              longitude: res.location!.lng(),
            },
            types: res.types,
          } satisfies AddressWithLocation;
        },
      } satisfies AutocompletePrediction;
    });
  return results;
}

export function AddressComponentsToAddressWithLocation(
  addrComp: google.maps.places.AddressComponent[],
): AddressFields {
  const addr: AddressFields = { ...defaultAddress };
  addrComp.forEach((address_component) => {
    address_component.types.forEach((type) => {
      if (!address_component.longText && !address_component.shortText) {
        return;
      }
      switch (type) {
        case "street_number": {
          addr.number =
            address_component.longText ?? address_component.shortText!;
          break;
        }
        case "route": {
          addr.street =
            address_component.longText ?? address_component.shortText!;
          break;
        }
        case "postal_town":
        case "locality": {
          addr.city =
            address_component.longText ?? address_component.shortText!;
          break;
        }
        case "administrative_area_level_1": {
          addr.state =
            address_component.longText ?? address_component.shortText!;
          break;
        }
        case "country": {
          addr.country =
            address_component.longText ?? address_component.shortText!;
          addr.countryISO3166Code = address_component.shortText ?? "";
          break;
        }
        case "postal_code": {
          addr.postal_code =
            address_component.longText ?? address_component.shortText!;
          break;
        }
      }
    });
  });

  return addr;
}
